import React from 'react';

import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';

import { TestCycleOption } from '@qality/common-model/src/TestCycle';
import { useOverflowObserver } from '@qality/common-utils';

import { ExecutionStatusLozenge } from '../../../atoms/ExecutionStatusLozenge';
import {
  DescriptionStyled,
  ExecutionDetailsWrapperStyled,
  LabelWrapperStyled,
  ProjectIconStyled,
  StatusDescriptionStyled,
  TestCycleInfoWrapperStyled,
  TextWithOverflowStyled,
  VerticalWrapperStyled,
  WrapperStyled,
} from './TestCaseForExecutionSelectOption.styled';

type Props = {
  option: TestCycleOption;
  isSelectedOption: boolean;
};

export default function TestCaseForExecutionSelectOption({
  option,
  isSelectedOption,
}: Props): JSX.Element {
  const { ref, isOverflowing } = useOverflowObserver();

  const getCreatedDateFormatted = (createdDate: string): string => {
    const date = new Date(createdDate);
    const day = date.getUTCDate();
    const month = date
      .toLocaleString('en-US', { month: 'short' })
      .toUpperCase();
    const year = date.getUTCFullYear();

    return `${day} ${month} ${year}`;
  };

  const isAdHocOption = option.value.projectId === null;

  if (isSelectedOption) {
    return (
      <TestCycleInfoWrapperStyled>
        {!isAdHocOption && (
          <ProjectIconStyled
            data-testid='test-cycle-project-icon'
            src={option.value.projectAvatarUrl}
          />
        )}
        <TextWithOverflowStyled width='500px'>
          {option.label}
        </TextWithOverflowStyled>
      </TestCycleInfoWrapperStyled>
    );
  }

  if (isAdHocOption) {
    return (
      <LabelWrapperStyled>
        <div>{option.label}</div>
        <DescriptionStyled>
          Execute this test case without test cycle
        </DescriptionStyled>
      </LabelWrapperStyled>
    );
  }

  return (
    <WrapperStyled>
      <TestCycleInfoWrapperStyled>
        <ProjectIconStyled
          data-testid='test-cycle-project-icon'
          src={option.value.projectAvatarUrl}
        />
        <LabelWrapperStyled>
          <Tooltip content={isOverflowing && option.label}>
            <TextWithOverflowStyled innerRef={ref} width='322px'>
              {option.label}
            </TextWithOverflowStyled>
          </Tooltip>
          <DescriptionStyled>
            Test cycle created:{' '}
            {getCreatedDateFormatted(option.value.createdDate)}
          </DescriptionStyled>
        </LabelWrapperStyled>
      </TestCycleInfoWrapperStyled>
      <VerticalWrapperStyled>
        <StatusDescriptionStyled>
          EXECUTION IN THIS CYCLE
        </StatusDescriptionStyled>
        <ExecutionDetailsWrapperStyled>
          <ExecutionStatusLozenge status={option.value.statusObject} />
          <Tooltip content={option.value.executionAssigneeName}>
            <Avatar
              size='small'
              src={option.value.executionAssigneeAvatarUrl}
              borderColor='var(--ds-soldevelo-aureola-border, var( --ds-fb-soldevelo-aureola-border))'
              testId='execution-assignee-avatar'
            />
          </Tooltip>
        </ExecutionDetailsWrapperStyled>
      </VerticalWrapperStyled>
    </WrapperStyled>
  );
}
